import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select from 'react-select';

import { useIsLoggedInQuery } from '../../../redux/apis/authApi';
import { useGetEmployeeDropDownQuery } from '../../../redux/apis/employee';
import { resetHrxAttendanceState } from '../../../redux/Slice/hrxAttendanceSlice';
import AttendanceTable from './components/AttendanceTable';
import PageHeader from '../../../components/common/PageHeader';
import AttendanceOverview from './components/AttendanceOverview';

import { skipRow } from '../../../utils/handler';
import { ADMIN_HR_ROLES } from '../../../utils/constant/global';

import './HRXAttendance.scss';

const initialState = {
  loading: false,
  currentPage: 1,
  rowsPerPage: 10,
  dropdownDataStore: [],
  currentPageDropdown: 1,
  rowsPerPageDropdown: 50,
  dropdownSearch: ''
};

const Attendance = () => {
  const dispatch = useDispatch();
  const { data: isLogin } = useIsLoggedInQuery();
  const [state, setState] = useState(initialState);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const isAdminOrHr = ADMIN_HR_ROLES.includes(isLogin?.data?.hrxRole?.roleName);

  const { data: dropdownData, isFetching: isEmployeeDropdownFetching } =
    useGetEmployeeDropDownQuery(
      {
        skip: skipRow(state.currentPageDropdown, state.rowsPerPageDropdown),
        limit: state.rowsPerPageDropdown,
        search: state.dropdownSearch
      },
      { skip: !isAdminOrHr }
    );

  useEffect(() => {
    return () => {
      dispatch(resetHrxAttendanceState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!dropdownData?.data) return;

    setState((prevState) => {
      const mergedData =
        prevState.currentPageDropdown === 1
          ? [...dropdownData.data]
          : [...prevState.dropdownDataStore, ...dropdownData.data];

      const uniqueData = mergedData.filter(
        (item, index, self) => self.findIndex((t) => t._id === item._id) === index
      );

      return { ...prevState, dropdownDataStore: uniqueData };
    });
  }, [dropdownData]);

  const employeeOptions = useMemo(
    () =>
      state.dropdownDataStore.map(({ _id, name }) => ({
        value: _id,
        label: name
      })),
    [state.dropdownDataStore]
  );

  const handleMenuScrollToBottom = () => {
    if (dropdownData?.isNext && !isEmployeeDropdownFetching) {
      setState((prevState) => ({
        ...prevState,
        currentPageDropdown: prevState.currentPageDropdown + 1
      }));
    }
  };

  const handleEmployeeInputChange = (text, meta) => {
    if (meta?.action !== 'input-change') return text;

    if (text && text.length > 2) {
      setState((prev) => ({
        ...prev,
        dropdownSearch: text,
        currentPageDropdown: 1,
        dropdownDataStore: []
      }));
    } else if (!text) {
      setState((prev) => ({
        ...prev,
        dropdownSearch: '',
        currentPageDropdown: 1,
        dropdownDataStore: []
      }));
    }

    return text;
  };

  return (
    <div className="container-xxl hrx-attendance-page">
      <div className="hrx-attendance-page__head">
        <PageHeader headerTitle={'HRX Attendance'} />
      </div>
      {isAdminOrHr && (
        <div className="hrx-attendance-page__picker card border-0 shadow-sm">
          <div className="card-body">
            <label className="form-label fw-semibold mb-2">
              Filter Employee <span className="text-muted fw-normal">(optional)</span>
            </label>
            <Select
              classNamePrefix="hrx-attendance-select"
              className="hrx-attendance-page__select w-100"
              options={employeeOptions}
              filterOption={null}
              isClearable
              isLoading={isEmployeeDropdownFetching}
              onInputChange={handleEmployeeInputChange}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              placeholder="All employees…"
              value={selectedEmployee}
              onChange={setSelectedEmployee}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              styles={{
                control: (provided) => ({
                  ...provided,
                  minWidth: '200px',
                  minHeight: 42,
                  borderRadius: 12
                }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menuList: (base) => ({ ...base, maxHeight: 260 })
              }}
            />
          </div>
        </div>
      )}

      <AttendanceOverview userId={selectedEmployee?.value || null} />
      <AttendanceTable userId={selectedEmployee?.value || null} />
    </div>
  );
};

export default Attendance;
